import { MarkerEvent } from 'components/Map/data/model'
import PdcMarker from 'components/Map/PdcMarker'
import Pdc, { PdcEvent } from 'components/Pdc'
import { PdcCardData } from 'components/Pdc/Pdc'
import { Position } from 'data/model/pdc'
import { RootState } from 'data/reducer'
import { setPdcItem } from 'data/reducer/pdc'
import { useAppSelector } from 'data/store/hooks'
import { mapConfig } from 'lib/config'
import { createDirectionsPath, MapType, Waypoint, WaypointType } from 'lib/directions'
import { PageType, useNavigation } from 'lib/hooks'
import { BaseLayerNames } from 'map/layers'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useMatch, useSearchParams } from 'react-router-dom'
import { parseMapSearchParam } from 'utils/router'

export interface PdcPageProps {
  markersGroup: H.map.Group
}

export const PdcPage = ({ markersGroup }: PdcPageProps): JSX.Element => {
  const dispatch = useDispatch()
  const { goTo, goBack } = useNavigation()
  const [searchParams] = useSearchParams()
  const defaultPath = useMatch(`/${mapConfig.url.path.pdc}/*`)
  const locationPath = useMatch(`/${mapConfig.url.path.location}`)
  const mapSearchParamStr = searchParams.get('map')

  const pdcItem = useAppSelector((state: RootState) => state.pdc.pdcItem)
  const userPosition = useAppSelector((state: RootState) => state.user.currentPosition)

  const mapSearchParam = useMemo(
    () => (mapSearchParamStr !== null ? parseMapSearchParam(mapSearchParamStr) : undefined),
    [mapSearchParamStr]
  )

  const pdcData: PdcCardData = {
    deepLinkMapType: toMapType(mapSearchParam?.baseLayer as BaseLayerNames),
    deepLinkZoomLevel: mapSearchParam?.zoom,
  }

  const mapSearchParamRef = useRef(mapSearchParam)

  const placeQuery: string | Position | undefined = useMemo(() => {
    if (locationPath !== null) {
      return {
        lat: mapSearchParamRef.current?.lat,
        lng: mapSearchParamRef.current?.lng,
      } as Position
    }

    if (defaultPath !== null) {
      return defaultPath.params['*']
    }

    return undefined
  }, [defaultPath, locationPath])

  useEffect(() => {
    if (placeQuery === undefined) {
      goTo({
        type: PageType.LANDING,
        url: '/',
      })
    }
  }, [goTo, placeQuery])

  const handleOnDirectionsClick = useCallback(
    (pdc: PdcEvent) => {
      // collect from pdc and my location
      const waypoints: Waypoint[] = [
        {
          lat: pdc.lat,
          lng: pdc.lng,
          name: encodeURIComponent(pdc.title),
          type: WaypointType.full,
        },
      ]

      if (userPosition.coordinates !== null) {
        waypoints.unshift({
          type: WaypointType.myLocation,
        })
      } else {
        waypoints.unshift({ type: WaypointType.empty })
      }

      const path = createDirectionsPath(waypoints)

      dispatch(setPdcItem(undefined))
      goTo({
        type: PageType.ROUTE_PLANNER,
        url: `/directions/${path}`,
      })
    },
    [dispatch, goTo, userPosition.coordinates]
  )

  const handlePdcOnClose = useCallback(() => {
    goBack()
  }, [goBack])

  const handlePdcMarkerCreation = useCallback(
    (marker: H.map.Marker): void => {
      markersGroup.removeAll()
      markersGroup.addObject(marker)
      markersGroup.dispatchEvent(new H.util.Event(MarkerEvent.MarkersUpdate))
    },
    [markersGroup]
  )

  const handleError = useCallback(() => {
    goTo({
      type: PageType.LANDING,
      url: '/',
    })
  }, [goTo])

  return (
    <>
      <Pdc
        id={placeQuery}
        onError={handleError}
        onActionClick={handleOnDirectionsClick}
        onCloseButtonClick={handlePdcOnClose}
        data={pdcData}
      />
      {pdcItem && pdcItem.position && (
        <PdcMarker
          onMarkerCreate={handlePdcMarkerCreation}
          lat={pdcItem.position.lat}
          lng={pdcItem.position.lng}
        />
      )}
    </>
  )
}

function toMapType(baseLayer: BaseLayerNames): MapType {
  switch (baseLayer) {
    case BaseLayerNames.OMV:
      return MapType.normal
    case BaseLayerNames.SATELLITE_TRAFFIC:
    case BaseLayerNames.SATELLITE:
      return MapType.satellite
    case BaseLayerNames.TRAFFIC:
      return MapType.traffic
    default:
      return MapType.normal
  }
}
